import { useEffect, useState } from 'react';

/**
 * Zoom-variant test-toggle. Låter user växla mellan flera olika
 * rendering-strategier för att jämföra UX. Static-mode-rendering
 * (Free/Advanced/Pro vid tier-default) påverkas inte av denna
 * inställning — den lever sitt eget liv via tier-derivd
 * `effectiveZoom.mode === 'static'`.
 *
 * Persisteras i localStorage så valet behålls mellan sessioner. Default
 * är `'tier-default'` (= ingen override).
 *
 * Varianter:
 *   - `tier-default`            — använd tier-derivd zoom (Free/Advanced/
 *                                 Pro=static, Max/Enterprise=dynamic-no-
 *                                 transition)
 *   - `force-static`            — tvinga static-mode för alla tiers
 *                                 (uniform shrink, FAST coord-rymd).
 *                                 ENDA varianten utan block-jump på zoom
 *                                 — block stannar i samma screen-pos och
 *                                 hela canvas-cardet krymper visuellt.
 *   - `dynamic-with-transition` — pre-split-klassikern: transform på
 *                                 canvas-root + transition. Block kan
 *                                 jumpa pga multiplikations-mismatch.
 *   - `dynamic-no-transition`   — instant-snap: transform på canvas-root
 *                                 utan transition. Inga animation-jumps
 *                                 men blocks teleporterar pga växande
 *                                 coord-rymd.
 *
 * Range för dynamic-varianter: 0.25-1.0 (Enterprise). 1.0 är hardlock
 * zoomMax — pre-split hade aldrig zoom-in över 100%.
 */
export type ZoomVariant =
  | 'tier-default'
  | 'force-static'
  | 'dynamic-with-transition'
  | 'dynamic-no-transition';

const STORAGE_KEY = 'fw-zoom-variant';

const VALID_VARIANTS: readonly ZoomVariant[] = [
  'tier-default',
  'force-static',
  'dynamic-with-transition',
  'dynamic-no-transition',
];

function readStored(): ZoomVariant {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw && (VALID_VARIANTS as readonly string[]).includes(raw)) {
      return raw as ZoomVariant;
    }
  } catch { /* localStorage disabled */ }
  return 'tier-default';
}

/**
 * Read + persist zoom-variant. Cross-tab sync via 'storage'-event.
 */
export function useZoomVariant(): [ZoomVariant, (v: ZoomVariant) => void] {
  const [variant, setVariantState] = useState<ZoomVariant>(readStored);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        if ((VALID_VARIANTS as readonly string[]).includes(e.newValue)) {
          setVariantState(e.newValue as ZoomVariant);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setVariant = (v: ZoomVariant) => {
    setVariantState(v);
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
    // Variantbytet kräver fullständig re-render av canvas-trädet eftersom
    // effectiveZoom + coordSpaceDivider + canvas-inner-styling beror på
    // variant-flaggor som inte kommer cleant att byta i en pågående DOM:
    // CSS-transitions, scroll-position, useState-init m.m. blir
    // inkonsistent. Page-reload garanterar ett rent canvas-state.
    // Skip om jsdom (test-miljö) — `window.location.reload` finns men
    // räcker det för att labil tester.
    if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
      // Ge React en frame att flusha state-uppdateringen + localStorage-write
      // innan reload, annars kan setItem-callen race:a unload.
      requestAnimationFrame(() => window.location.reload());
    }
  };

  return [variant, setVariant];
}

export const ZOOM_VARIANT_LABELS: Record<ZoomVariant, string> = {
  'tier-default': 'Tier-default',
  'force-static': 'Static (uniform shrink) — inga block-jumps',
  'dynamic-with-transition': 'Dynamic (pre-split, transition)',
  'dynamic-no-transition': 'Dynamic (no transition, snap)',
};
